import { useState, useMemo } from 'react';
import { X, Search, Book, Package, Box, MapPin, DollarSign, Settings, Rocket, ChevronRight, HelpCircle } from 'lucide-react';
import { helpCategories, helpArticles, searchHelpArticles, getArticlesByCategory, type HelpArticle } from '@/data/helpContent';
import ReactMarkdown from 'react-markdown';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialArticle?: string;
}

const iconMap: Record<string, React.ElementType> = {
  Rocket,
  Package,
  Box,
  MapPin,
  DollarSign,
  Settings,
  Book,
};

export function HelpModal({ isOpen, onClose, initialArticle }: HelpModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<string | null>(initialArticle || null);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchHelpArticles(searchQuery);
  }, [searchQuery]);

  // Category articles
  const categoryArticles = useMemo(() => {
    if (!selectedCategory) return [];
    return getArticlesByCategory(selectedCategory);
  }, [selectedCategory]);

  // Current article content
  const currentArticle = useMemo(() => {
    return selectedArticle ? helpArticles[selectedArticle] : null;
  }, [selectedArticle]);

  const handleArticleClick = (articleId: string) => {
    setSelectedArticle(articleId);
  };

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSearchQuery('');
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedArticle(null);
    setSelectedCategory(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 dark:bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-gray-50 dark:bg-gray-50 dark:bg-charcoal-850">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-450/10 border border-amber-450/20 flex items-center justify-center">
              <Book className="w-5 h-5 text-amber-450" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-gray-900 dark:text-warm-cream">
                Help Center
              </h2>
              <p className="text-xs text-gray-500 dark:text-warm-sand">
                Documentation & Guides
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-charcoal-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value) {
                    setSelectedCategory(null);
                    setSelectedArticle(null);
                  }
                }}
                placeholder="Search help..."
                className="w-64 pl-9 pr-4 py-2 bg-gray-100 dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:border-amber-450"
              />
            </div>
            
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500 dark:text-warm-sand" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar - Categories */}
          {!selectedArticle && (
            <div className="w-64 border-r border-gray-200 dark:border-charcoal-700 bg-white/50 dark:bg-charcoal-800/50 overflow-y-auto">
              <div className="p-4">
                <button
                  onClick={clearSearch}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    !selectedCategory && !searchQuery
                      ? 'bg-amber-450/10 text-amber-450'
                      : 'text-gray-600 dark:text-warm-sand hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700'
                  }`}
                >
                  All Categories
                </button>
                
                <div className="mt-4 space-y-1">
                  {helpCategories.map((category) => {
                    const Icon = iconMap[category.icon] || Book;
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryClick(category.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedCategory === category.id
                            ? 'bg-amber-450/10 text-amber-450'
                            : 'text-gray-600 dark:text-warm-sand hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {category.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Breadcrumb */}
            {(selectedCategory || selectedArticle) && (
              <div className="flex items-center gap-2 mb-6 text-sm">
                <button
                  onClick={handleBack}
                  className="text-gray-600 dark:text-warm-sand hover:text-amber-450 transition-colors"
                >
                  Help Center
                </button>
                {selectedCategory && !selectedArticle && (
                  <>
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-charcoal-500" />
                    <span className="text-amber-450">
                      {helpCategories.find(c => c.id === selectedCategory)?.title}
                    </span>
                  </>
                )}
                {selectedArticle && currentArticle && (
                  <>
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-charcoal-500" />
                    <button
                      onClick={() => setSelectedArticle(null)}
                      className="text-gray-600 dark:text-warm-sand hover:text-amber-450 transition-colors"
                    >
                      {helpCategories.find(c => c.id === currentArticle.category)?.title}
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-charcoal-500" />
                    <span className="text-amber-450">{currentArticle.title}</span>
                  </>
                )}
              </div>
            )}

            {/* Search Results */}
            {searchQuery && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream mb-4">
                  Search Results for "{searchQuery}"
                </h3>
                {searchResults.length === 0 ? (
                  <div className="text-center py-12">
                    <HelpCircle className="w-12 h-12 text-gray-400 dark:text-charcoal-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-warm-sand">No articles found matching "{searchQuery}"</p>
                    <p className="text-sm text-gray-400 dark:text-charcoal-500 mt-2">
                      Try different keywords or browse categories
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {searchResults.map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        onClick={() => handleArticleClick(article.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Category View */}
            {selectedCategory && !searchQuery && !selectedArticle && (
              <div>
                {(() => {
                  const category = helpCategories.find(c => c.id === selectedCategory);
                  if (!category) return null;
                  const Icon = iconMap[category.icon] || Book;
                  
                  return (
                    <>
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-amber-450/10 border border-amber-450/20 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-amber-450" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-warm-cream">
                            {category.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-warm-sand">{category.description}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {categoryArticles.map((article) => (
                          <ArticleCard
                            key={article.id}
                            article={article}
                            onClick={() => handleArticleClick(article.id)}
                          />
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Article View */}
            {selectedArticle && currentArticle && (
              <div className="max-w-3xl">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-warm-cream mb-2">
                  {currentArticle.title}
                </h1>
                <p className="text-gray-600 dark:text-warm-sand mb-6">{currentArticle.summary}</p>
                
                <div className="prose prose-invert prose-charcoal max-w-none">
                  <ReactMarkdown
                    components={{
                      h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-cream mt-8 mb-4">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream mt-6 mb-3">{children}</h3>,
                      p: ({ children }) => <p className="text-gray-600 dark:text-warm-sand mb-4 leading-relaxed">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc list-inside text-gray-600 dark:text-warm-sand mb-4 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside text-gray-600 dark:text-warm-sand mb-4 space-y-1">{children}</ol>,
                      li: ({ children }) => <li className="ml-4">{children}</li>,
                      code: ({ children }) => <code className="bg-gray-100 dark:bg-charcoal-800 px-2 py-0.5 rounded text-amber-450 text-sm">{children}</code>,
                      pre: ({ children }) => <pre className="bg-gray-100 dark:bg-charcoal-800 p-4 rounded-lg overflow-x-auto mb-4">{children}</pre>,
                      table: ({ children }) => <table className="w-full border-collapse mb-4">{children}</table>,
                      th: ({ children }) => <th className="border border-gray-200 dark:border-charcoal-700 bg-gray-100 dark:bg-charcoal-800 px-4 py-2 text-left text-gray-900 dark:text-warm-cream font-semibold">{children}</th>,
                      td: ({ children }) => <td className="border border-gray-200 dark:border-charcoal-700 px-4 py-2 text-gray-600 dark:text-warm-sand">{children}</td>,
                      strong: ({ children }) => <strong className="text-gray-900 dark:text-warm-cream font-semibold">{children}</strong>,
                      blockquote: ({ children }) => <blockquote className="border-l-4 border-amber-450 pl-4 italic text-gray-600 dark:text-warm-sand my-4">{children}</blockquote>,
                    }}
                  >
                    {currentArticle.content}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* Home View - All Categories */}
            {!selectedCategory && !searchQuery && !selectedArticle && (
              <div>
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-warm-cream mb-2">
                    How can we help you?
                  </h3>
                  <p className="text-gray-600 dark:text-warm-sand">
                    Search for answers or browse by category
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {helpCategories.map((category) => {
                    const Icon = iconMap[category.icon] || Book;
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryClick(category.id)}
                        className="flex items-start gap-4 p-4 bg-white/50 dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl hover:border-amber-450/50 transition-all text-left"
                      >
                        <div className="w-10 h-10 rounded-lg bg-amber-450/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-amber-450" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-warm-cream mb-1">
                            {category.title}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-warm-sand">
                            {category.description}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-charcoal-500 mt-2">
                            {category.articles.length} articles
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Quick Links */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-charcoal-700">
                  <h4 className="font-semibold text-gray-900 dark:text-warm-cream mb-4">Popular Topics</h4>
                  <div className="flex flex-wrap gap-2">
                    {['supplies-overview', 'supply-types', 'supply-categories', 'sku-guide', 'bom-setup'].map((articleId) => {
                      const article = helpArticles[articleId];
                      if (!article) return null;
                      return (
                        <button
                          key={articleId}
                          onClick={() => handleArticleClick(articleId)}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-charcoal-800 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 border border-gray-200 dark:border-charcoal-700 rounded-lg text-sm text-gray-600 dark:text-warm-sand transition-colors"
                        >
                          {article.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Article Card Component
function ArticleCard({ article, onClick }: { article: HelpArticle; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 bg-white/50 dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl hover:border-amber-450/50 transition-all"
    >
      <h4 className="font-semibold text-gray-900 dark:text-warm-cream mb-1">{article.title}</h4>
      <p className="text-sm text-gray-600 dark:text-warm-sand">{article.summary}</p>
    </button>
  );
}
